import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, PawPrint, Heart } from 'lucide-react';

const HERO_ROTATE_INTERVAL = 4000;

export const HeroRotator = ({ tools, onSelect, customer }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || tools.length <= 1) return;
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % tools.length);
    }, HERO_ROTATE_INTERVAL);
    return () => clearInterval(id);
  }, [paused, tools.length]);

  if (!tools.length) return null;
  const safeIndex = index % tools.length;
  const tool = tools[safeIndex];
  const Icon = tool.icon;
  const badgeLabel = {
    'sample-box': 'Sample Box',
    'ted-ai': 'Ted AI Scan',
    'get-matched': 'Dog Match',
    'account': customer ? 'My Account' : 'Sign In',
  }[tool.id] || tool.cta;

  return (
    <div
      className="relative mx-auto max-w-xs"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-rotator"
    >
      {/* Glow */}
      <div className="absolute inset-0 bg-[#a0da39]/25 blur-3xl rounded-full" />

      {/* Rotating card */}
      <div className="relative bg-gradient-to-br from-white to-[#f4f7ea] rounded-2xl shadow-2xl p-4 border-4 border-[#a0da39] transform lg:rotate-1 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-[#1f6d62] bg-[#a0da39] px-2 py-0.5 rounded-full">
            <Sparkles className="w-2.5 h-2.5" /> Explore
          </span>
          <span className="text-[9px] font-bold uppercase text-[#1f6d62]/70" data-testid="hero-rotator-badge">
            {badgeLabel}
          </span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tool.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45 }}
          >
            <div className="h-32 rounded-xl overflow-hidden mb-3 bg-gradient-to-br from-[#1f6d62] to-[#14544b] relative">
              <img
                src={tool.image}
                alt={tool.title}
                className="w-full h-full object-cover opacity-95"
              />
              <div className="absolute bottom-1.5 left-1.5 right-1.5 bg-white/90 backdrop-blur-sm rounded-md px-2 py-1 flex items-center justify-between">
                <span className="text-[10px] font-bold text-[#1f6d62] uppercase truncate">
                  {tool.title}
                </span>
                <Icon className="w-3.5 h-3.5 text-[#0FA49C] flex-shrink-0 ml-1" />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <button
          onClick={() => onSelect(tool)}
          className="w-full bg-[#1f6d62] hover:bg-[#174f46] text-white font-extrabold uppercase tracking-wide py-2.5 rounded-full shadow-md transition-all duration-200 text-xs flex items-center justify-center gap-2"
          data-testid={`hero-rotator-cta-${tool.id}`}
        >
          {tool.cta}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>

        {/* Dot indicators */}
        <div className="mt-3 flex items-center justify-center gap-1.5">
          {tools.map((t, i) => (
            <button
              key={t.id}
              onClick={() => setIndex(i)}
              aria-label={`Show ${t.title}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === safeIndex ? 'w-5 bg-[#1f6d62]' : 'w-1.5 bg-[#1f6d62]/25 hover:bg-[#1f6d62]/50'
              }`}
              data-testid={`hero-rotator-dot-${t.id}`}
            />
          ))}
        </div>
      </div>

      {/* Floating accents */}
      <div className="absolute -top-2 -left-2 w-8 h-8 bg-[#a0da39] rounded-full flex items-center justify-center shadow-lg">
        <PawPrint className="w-4 h-4 text-[#1f3d2f]" />
      </div>
      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
        <Heart className="w-4 h-4 text-[#1f6d62]" />
      </div>
    </div>
  );
};
